import React, { useState, useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import * as QRCode from "qrcode";
import toast from "react-hot-toast";
import { PDFDocument } from "pdf-lib";
import {
  IconBarcode,
  IconQrcode,
  IconFileTypePng,
  IconFileTypeJpg,
  IconFileTypePdf,
  IconDownload,
  IconUpload,
  IconFileSpreadsheet,
} from "@tabler/icons-react";

type ActiveTab = "barcode" | "qr";
type ExportFormat = "PNG" | "JPG" | "PDF";
type ECCLevel = "L" | "M" | "Q" | "H";

const BARCODE_TYPES = [
  { value: "CODE128", label: "CODE128 — Universal alphanumeric" },
  { value: "CODE39", label: "CODE39 — Industrial / vintage" },
  { value: "EAN13", label: "EAN-13 — Retail standard" },
  { value: "EAN8", label: "EAN-8 — Small product labels" },
  { value: "UpcA", label: "UPC-A — North American retail" },
  { value: "ITF14", label: "ITF-14 — Shipping / logistics" },
  { value: "MSI", label: "MSI — Warehouse / inventory" },
  { value: "pharmacode", label: "Pharmacode — Pharmaceutical" },
];

export default function BarcodeQrGenerator() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("barcode");
  const [exportFormat, setExportFormat] = useState<ExportFormat>("PNG");

  // Hidden File Input Ref for CSV Upload
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  // Fixed Minimal Colors (#000000 / #ffffff)
  const fgColor = "#000000";
  const bgColor = "#ffffff";

  // Barcode options
  const [barcodeType, setBarcodeType] = useState("CODE128");
  const [barcodeValuesText, setBarcodeValuesText] = useState(
    "PHOTOSHOP2025\nORDER-1001\nSHIP-99201"
  );
  const [barWidth, setBarWidth] = useState<number>(2);
  const [barHeight, setBarHeight] = useState<number>(80);
  const [barcodeMargin, setBarcodeMargin] = useState<number>(10);
  const [barcodeFontSize, setBarcodeFontSize] = useState<number>(14);
  const [showBarcodeText, setShowBarcodeText] = useState(true);

  // QR Code options
  const [qrValuesText, setQrValuesText] = useState(
    "https://adobe.com\nhttps://example.com"
  );
  const [eccLevel, setEccLevel] = useState<ECCLevel>("M");
  const [qrSize, setQrSize] = useState<number>(200);
  const [qrFontSize, setQrFontSize] = useState<number>(14);

  // CSV File Upload Handler
  const handleCsvFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) return;

      const rawLines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

      if (rawLines.length === 0) {
        toast.error("The uploaded CSV file is empty!");
        return;
      }

      // Extract values (handle commas / quotes if multi-column)
      const parsedValues = rawLines.map((line) => {
        const parts = line
          .split(",")
          .map((p) => p.replace(/^["']|["']$/g, "").trim());
        return parts.find((p) => p.length > 0) || line;
      });

      // Skip header row if header names match common column names
      const firstValLower = parsedValues[0].toLowerCase();
      const headerNames = [
        "value",
        "values",
        "url",
        "urls",
        "text",
        "barcode",
        "barcodes",
        "code",
        "data",
        "qr_code",
        "barcode_value",
        "url_or_text",
      ];
      const finalValues = headerNames.includes(firstValLower)
        ? parsedValues.slice(1)
        : parsedValues;

      if (finalValues.length === 0) {
        toast.error("No valid data rows found in CSV file!");
        return;
      }

      const combinedText = finalValues.join("\n");
      if (activeTab === "barcode") {
        setBarcodeValuesText(combinedText);
      } else {
        setQrValuesText(combinedText);
      }

      toast.success(`Imported ${finalValues.length} values from CSV file!`);
      if (event.target) event.target.value = "";
    };
    reader.readAsText(file);
  };

  // Download Sample CSV Template
  const handleDownloadSampleCsv = () => {
    const isBarcode = activeTab === "barcode";
    const sampleContent = isBarcode
      ? "Value\nPHOTOSHOP2025\nORDER-1001\nSHIP-99201\nITEM-8802\nBARCODE-9941"
      : "URL_Or_Text\nhttps://example.com\nhttps://google.com\nhttps://adobe.com\nSHIP-ORDER-2025";

    const blob = new Blob([sampleContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `sample_${isBarcode ? "barcodes" : "qrcodes"}.csv`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded sample CSV template!");
  };

  // Parse lines into item arrays
  const barcodeItems = barcodeValuesText
    .split("\n")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  const qrItems = qrValuesText
    .split("\n")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  // Refs for rendering
  const barcodeSvgRefs = useRef<(SVGSVGElement | null)[]>([]);
  const qrCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const [renderErrors, setRenderErrors] = useState<Record<number, string>>({});

  // Render Barcodes
  useEffect(() => {
    if (activeTab !== "barcode") return;
    const newErrors: Record<number, string> = {};

    barcodeItems.forEach((val, idx) => {
      const svgEl = barcodeSvgRefs.current[idx];
      if (!svgEl) return;
      try {
        svgEl.innerHTML = "";
        JsBarcode(svgEl, val, {
          format: barcodeType,
          lineColor: fgColor,
          background: bgColor,
          width: barWidth || 2,
          height: barHeight || 80,
          displayValue: showBarcodeText,
          fontSize: barcodeFontSize || 14,
          margin: barcodeMargin || 10,
          font: "monospace",
          valid: (valid: boolean) => {
            if (!valid) {
              newErrors[idx] = `Invalid value for ${barcodeType}`;
            }
          },
        });
      } catch (err: any) {
        newErrors[idx] = err?.message || "Invalid configuration";
      }
    });

    setRenderErrors(newErrors);
  }, [
    activeTab,
    barcodeValuesText,
    barcodeType,
    barWidth,
    barHeight,
    barcodeMargin,
    showBarcodeText,
    barcodeFontSize,
  ]);

  // Render QR Codes
  useEffect(() => {
    if (activeTab !== "qr") return;
    const newErrors: Record<number, string> = {};

    qrItems.forEach((val, idx) => {
      const canvasEl = qrCanvasRefs.current[idx];
      if (!canvasEl) return;
      try {
        QRCode.toCanvas(
          canvasEl,
          val,
          {
            width: qrSize || 200,
            margin: 2,
            errorCorrectionLevel: eccLevel,
            color: {
              dark: fgColor,
              light: bgColor,
            },
          },
          (err: any) => {
            if (err) {
              newErrors[idx] = err?.message || "Invalid QR configuration";
            }
          }
        );
      } catch (err: any) {
        newErrors[idx] = err?.message || "Invalid QR configuration";
      }
    });

    setRenderErrors(newErrors);
  }, [activeTab, qrValuesText, eccLevel, qrSize]);

  // Helper: SVG to Canvas
  const renderSvgToCanvas = (
    svgEl: SVGSVGElement,
    scale = 3
  ): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
      const xml = new XMLSerializer().serializeToString(svgEl);
      const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas context failed"));

        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas);
      };
      img.onerror = (e) => reject(e);
      img.src = url;
    });
  };

  // Export Logic
  const handleExport = async () => {
    const isBarcode = activeTab === "barcode";
    const items = isBarcode ? barcodeItems : qrItems;

    if (items.length === 0) {
      toast.error("Please enter at least one value to export!");
      return;
    }

    try {
      // Gather image data URLs for all items
      const imageDataUrls: { label: string; dataUrl: string; width: number; height: number }[] = [];

      for (let i = 0; i < items.length; i++) {
        if (isBarcode) {
          const svgEl = barcodeSvgRefs.current[i];
          if (!svgEl) continue;
          const canvas = await renderSvgToCanvas(svgEl, 3);
          const mime = exportFormat === "JPG" ? "image/jpeg" : "image/png";
          imageDataUrls.push({
            label: items[i],
            dataUrl: canvas.toDataURL(mime, 0.95),
            width: canvas.width,
            height: canvas.height,
          });
        } else {
          const canvasEl = qrCanvasRefs.current[i];
          if (!canvasEl) continue;
          const mime = exportFormat === "JPG" ? "image/jpeg" : "image/png";
          imageDataUrls.push({
            label: items[i],
            dataUrl: canvasEl.toDataURL(mime, 0.95),
            width: canvasEl.width,
            height: canvasEl.height,
          });
        }
      }

      if (imageDataUrls.length === 0) {
        toast.error("No valid items rendered to export");
        return;
      }

      if (exportFormat === "PDF") {
        // Build PDF document using pdf-lib
        const pdfDoc = await PDFDocument.create();

        for (const item of imageDataUrls) {
          const isJpeg = item.dataUrl.startsWith("data:image/jpeg");
          const image = isJpeg
            ? await pdfDoc.embedJpg(item.dataUrl)
            : await pdfDoc.embedPng(item.dataUrl);

          // Add page suited to content size with padding
          const padding = 40;
          const labelSpace = 30;
          const pageWidth = Math.max(item.width / 2, 300) + padding * 2;
          const pageHeight = Math.max(item.height / 2, 150) + padding * 2 + labelSpace;

          const page = pdfDoc.addPage([pageWidth, pageHeight]);

          const drawWidth = Math.min(item.width / 2, pageWidth - padding * 2);
          const drawHeight = Math.min(item.height / 2, pageHeight - padding * 2 - labelSpace);
          const posX = (pageWidth - drawWidth) / 2;
          const posY = (pageHeight - drawHeight - labelSpace) / 2 + labelSpace;

          page.drawImage(image, {
            x: posX,
            y: posY,
            width: drawWidth,
            height: drawHeight,
          });
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes.buffer as ArrayBuffer], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `${isBarcode ? "barcodes" : "qrcodes"}_export.pdf`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${imageDataUrls.length} items to PDF!`);
      } else {
        // Single or Batch PNG / JPG download
        imageDataUrls.forEach((item, index) => {
          const link = document.createElement("a");
          const ext = exportFormat === "JPG" ? "jpg" : "png";
          const safeName = item.label.replace(/[^a-zA-Z0-9_-]/g, "_");
          link.download = `${isBarcode ? "barcode" : "qrcode"}_${safeName || index + 1}.${ext}`;
          link.href = item.dataUrl;
          link.click();
        });
        toast.success(`Exported ${imageDataUrls.length} ${exportFormat} file(s)!`);
      }
    } catch (err) {
      toast.error("Export failed. Please check your inputs.");
    }
  };

  return (
    <div className="barcode-qr-container p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      <style>{`
        .barcode-qr-container input[type="text"],
        .barcode-qr-container input[type="number"],
        .barcode-qr-container textarea,
        .barcode-qr-container select {
          background-color: #ffffff !important;
          color: #000000 !important;
          -webkit-text-fill-color: #000000 !important;
          border: 1.5px solid #64748b !important;
          font-weight: 700 !important;
          opacity: 1 !important;
        }

        .barcode-qr-container input[type="text"]:-webkit-autofill,
        .barcode-qr-container input[type="number"]:-webkit-autofill,
        .barcode-qr-container textarea:-webkit-autofill,
        .barcode-qr-container select:-webkit-autofill {
          -webkit-text-fill-color: #000000 !important;
          -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
          box-shadow: 0 0 0px 1000px #ffffff inset !important;
        }

        .barcode-qr-container select option {
          background-color: #ffffff !important;
          color: #000000 !important;
        }
      `}</style>
      {/* Hidden CSV File Input */}
      <input
        type="file"
        ref={csvFileInputRef}
        className="hidden"
        accept=".csv,.txt"
        onChange={handleCsvFileUpload}
      />

      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-slate-200 text-[#1E293B] rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#FBD037] rounded-lg shadow-sm">
            <IconBarcode size={28} className="text-black" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-[#1E293B]">Barcode & QR Generator</h1>
            <p className="text-xs text-slate-500">
              Generate & export single or batch barcodes and QR codes in PNG, JPG, or PDF.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center bg-[#F1F5F9] p-1 rounded-lg border border-slate-200">
          <button
            onClick={() => setActiveTab("barcode")}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-md transition-all ${
              activeTab === "barcode"
                ? "bg-black text-white shadow-sm"
                : "text-slate-600 hover:text-black hover:bg-slate-200/60"
            }`}
          >
            <IconBarcode size={16} />
            Barcode
          </button>
          <button
            onClick={() => setActiveTab("qr")}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-md transition-all ${
              activeTab === "qr"
                ? "bg-black text-white shadow-sm"
                : "text-slate-600 hover:text-black hover:bg-slate-200/60"
            }`}
          >
            <IconQrcode size={16} />
            QR Code
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Controls Sidebar (5 cols) */}
        <div className="lg:col-span-5 space-y-5 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          {activeTab === "barcode" ? (
            /* Barcode Controls */
            <div className="space-y-4">
              <h2 className="text-xs font-bold text-[#1E293B] uppercase tracking-wider border-b pb-2 border-slate-100">
                Barcode Configuration
              </h2>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Barcode Type
                </label>
                <select
                  value={barcodeType}
                  onChange={(e) => setBarcodeType(e.target.value)}
                  className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-yellow-50 text-slate-900 focus:outline-none focus:border-[#FBD037]"
                >
                  {BARCODE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <label className="block text-xs font-semibold text-slate-700">
                      Values / Text (Multiple lines supported)
                    </label>
                    <span className="text-[10px] text-slate-500 font-medium">
                      ({barcodeItems.length} item{barcodeItems.length === 1 ? "" : "s"})
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => csvFileInputRef.current?.click()}
                      className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md bg-black text-white hover:bg-slate-800 transition-all shadow-sm active:scale-[0.98]"
                      title="Upload a CSV file to import values automatically"
                    >
                      <IconUpload size={14} />
                      Upload CSV
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadSampleCsv}
                      className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-200 transition-all active:scale-[0.98]"
                      title="Download sample CSV template"
                    >
                      <IconFileSpreadsheet size={14} />
                      Sample CSV
                    </button>
                  </div>
                </div>
                <textarea
                  rows={4}
                  value={barcodeValuesText}
                  onChange={(e) => setBarcodeValuesText(e.target.value)}
                  className="w-full text-xs p-3 border border-slate-300 rounded-lg bg-yellow-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#FBD037] font-mono"
                  placeholder="Enter barcode values (one per line)..."
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Enter multiple lines to generate batch barcodes.
                </p>
              </div>

              {/* Numeric Input Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Bar Width (px)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    step={0.5}
                    value={barWidth}
                    onChange={(e) => setBarWidth(Number(e.target.value))}
                    className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-yellow-50 text-slate-900 focus:outline-none focus:border-[#FBD037] font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Bar Height (px)
                  </label>
                  <input
                    type="number"
                    min={20}
                    max={300}
                    step={5}
                    value={barHeight}
                    onChange={(e) => setBarHeight(Number(e.target.value))}
                    className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-yellow-50 text-slate-900 focus:outline-none focus:border-[#FBD037] font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Margin (px)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={50}
                    step={1}
                    value={barcodeMargin}
                    onChange={(e) => setBarcodeMargin(Number(e.target.value))}
                    className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-yellow-50 text-slate-900 focus:outline-none focus:border-[#FBD037] font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Font Size (px)
                  </label>
                  <input
                    type="number"
                    min={8}
                    max={36}
                    step={1}
                    value={barcodeFontSize}
                    onChange={(e) => setBarcodeFontSize(Number(e.target.value))}
                    disabled={!showBarcodeText}
                    className={`w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-yellow-50 text-slate-900 focus:outline-none focus:border-[#FBD037] font-mono ${
                      !showBarcodeText ? "opacity-40 cursor-not-allowed" : ""
                    }`}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <label className="text-xs font-semibold text-slate-700">
                  Show Text Below Barcode
                </label>
                <input
                  type="checkbox"
                  checked={showBarcodeText}
                  onChange={(e) => setShowBarcodeText(e.target.checked)}
                  className="w-4 h-4 text-black rounded focus:ring-black accent-black"
                />
              </div>
            </div>
          ) : (
            /* QR Code Controls */
            <div className="space-y-4">
              <h2 className="text-xs font-bold text-[#1E293B] uppercase tracking-wider border-b pb-2 border-slate-100">
                QR Code Configuration
              </h2>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2">
                    <label className="block text-xs font-semibold text-slate-700">
                      URLs / Text (Multiple lines supported)
                    </label>
                    <span className="text-[10px] text-slate-500 font-medium">
                      ({qrItems.length} item{qrItems.length === 1 ? "" : "s"})
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => csvFileInputRef.current?.click()}
                      className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md bg-black text-white hover:bg-slate-800 transition-all shadow-sm active:scale-[0.98]"
                      title="Upload a CSV file to import values automatically"
                    >
                      <IconUpload size={14} />
                      Upload CSV
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadSampleCsv}
                      className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-200 transition-all active:scale-[0.98]"
                      title="Download sample CSV template"
                    >
                      <IconFileSpreadsheet size={14} />
                      Sample CSV
                    </button>
                  </div>
                </div>
                <textarea
                  rows={4}
                  value={qrValuesText}
                  onChange={(e) => setQrValuesText(e.target.value)}
                  className="w-full text-xs p-3 border border-slate-300 rounded-lg bg-yellow-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#FBD037] font-mono"
                  placeholder="Enter URLs or text (one per line)..."
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Enter multiple lines to generate batch QR codes.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Error Correction Level
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {(["L", "M", "Q", "H"] as ECCLevel[]).map((lvl) => (
                    <button
                      key={lvl}
                      onClick={() => setEccLevel(lvl)}
                      className={`py-2 text-xs font-bold rounded-lg border transition-all ${
                        eccLevel === lvl
                          ? "bg-black text-white border-black shadow-sm"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  L = 7% • M = 15% • Q = 25% • H = 30% recovery capacity
                </p>
              </div>

              {/* Numeric Input Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    QR Size (px)
                  </label>
                  <input
                    type="number"
                    min={80}
                    max={500}
                    step={10}
                    value={qrSize}
                    onChange={(e) => setQrSize(Number(e.target.value))}
                    className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-yellow-50 text-slate-900 focus:outline-none focus:border-[#FBD037] font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Label Font Size (px)
                  </label>
                  <input
                    type="number"
                    min={8}
                    max={36}
                    step={1}
                    value={qrFontSize}
                    onChange={(e) => setQrFontSize(Number(e.target.value))}
                    className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg bg-yellow-50 text-slate-900 focus:outline-none focus:border-[#FBD037] font-mono"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Export Format Selector (PNG, JPG, PDF) */}
          <div className="space-y-3 pt-3 border-t border-slate-100">
            <label className="block text-xs font-bold text-[#1E293B] uppercase tracking-wider">
              Export Format
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(["PNG", "JPG", "PDF"] as ExportFormat[]).map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => setExportFormat(fmt)}
                  className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold border transition-all ${
                    exportFormat === fmt
                      ? "bg-black text-white border-black shadow-sm"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                  }`}
                >
                  {fmt === "PNG" && <IconFileTypePng size={16} />}
                  {fmt === "JPG" && <IconFileTypeJpg size={16} />}
                  {fmt === "PDF" && <IconFileTypePdf size={16} />}
                  {fmt}
                </button>
              ))}
            </div>
          </div>

          {/* Main Action Export Button */}
          <div className="pt-2">
            <button
              onClick={handleExport}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-xs font-bold uppercase tracking-wider bg-black hover:bg-slate-800 text-white shadow-md active:scale-[0.99] transition-all"
            >
              <IconDownload size={18} />
              Export {exportFormat} ({activeTab === "barcode" ? barcodeItems.length : qrItems.length} Item
              {((activeTab === "barcode" ? barcodeItems.length : qrItems.length) > 1 ? "s" : "")})
            </button>
          </div>
        </div>

        {/* Right Live Preview Grid Panel (7 cols) */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col justify-between min-h-[450px]">
          <div className="flex items-center justify-between border-b pb-3 border-slate-100 mb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Live Preview Panel
            </span>
            <span className="text-xs text-slate-500 font-medium">
              {activeTab === "barcode"
                ? `${barcodeType} (${barcodeItems.length} Barcodes)`
                : `QR Code ECC-${eccLevel} (${qrItems.length} Codes)`}
            </span>
          </div>

          {/* Scrollable Items Container */}
          <div className="flex-1 overflow-y-auto space-y-6 max-h-[520px] pr-2">
            {(activeTab === "barcode" ? barcodeItems : qrItems).length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-xs">
                No items to preview. Enter text or URLs in the left panel.
              </div>
            ) : (
              (activeTab === "barcode" ? barcodeItems : qrItems).map((val, idx) => (
                <div
                  key={`${activeTab}-${idx}`}
                  className="p-6 bg-[#F8F9FA] rounded-xl border border-slate-200 shadow-sm flex flex-col items-center justify-center relative"
                >
                  <div className="absolute top-2 left-3 text-[10px] font-mono text-slate-400">
                    #{idx + 1}
                  </div>

                  {renderErrors[idx] ? (
                    <div className="text-rose-500 text-xs font-semibold p-3 bg-rose-50 rounded-lg">
                      ⚠️ {renderErrors[idx]}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      {activeTab === "barcode" ? (
                        <svg
                          ref={(el) => {
                            barcodeSvgRefs.current[idx] = el;
                          }}
                          className="max-w-full"
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <canvas
                            ref={(el) => {
                              qrCanvasRefs.current[idx] = el;
                            }}
                            className="max-w-full"
                          />
                          {val && (
                            <span
                              className="font-mono text-slate-700 max-w-xs truncate"
                              style={{ fontSize: `${qrFontSize}px` }}
                            >
                              {val}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Stats Bar */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-mono">
            <span>Minimal Theme (#000000 / #ffffff)</span>
            <span>
              Total: {activeTab === "barcode" ? barcodeItems.length : qrItems.length} item(s)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
